<template>
  <div
    :class="separator ? 'nav-item-separator' : 'nav-item'"
    @mouseenter="hoverButton(true)"
    @mouseleave="hoverButton(false)"
  >
    <div v-if="separator" class="separator" />
    <button
      v-else
      :class="`nav-button ${isActive && 'nav-button-active'} ${path ? '' : 'cursor-default'}`"
      @click="itemClicked({ path, title })"
    >
      <div class="position-relative">
        <Icon
          class="test"
          :icon="icon"
          :size="24"
          variant="outlined"
          :color="isActive ? '#334155' : '#94A3B8'"
        />
        <div class="notifications__count" v-if="notificationCount">{{ notificationCount }}</div>
      </div>
      <div class="medium-body-sm margin-top_sx-df">{{ title }}</div>
    </button>
    <div v-if="subItems.length" class="nav-subitems-container" :class="{ opened: buttonHovered }">
      <div class="nav-subitems">
        <div
          v-for="subItem in subItems"
          :key="subItem.path"
          class="nav-subitem"
          :class="{ hover: subItem?.path === hoveredItem?.path }"
          @click="itemClicked(subItem)"
          @keydown="itemClicked(subItem)"
          @mouseenter="hover(subItem)"
        >
          <span>{{ subItem.title }}</span>
        </div>
      </div>
      <div v-if="hoveredSubItems.length" class="nav-subitems second-level">
        <div
          v-for="subItem in hoveredSubItems"
          :key="subItem.path"
          class="nav-subitem"
          :class="subItem.disabled ? 'text-disabled' : ''"
          @click="itemClicked(subItem)"
          @keydown="itemClicked(subItem)"
        >
          <p>
            {{ subItem.title }}
            <component :is="subItem.leadingComponent" />
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from 'vue';
import Icon from '@design_system_v2/atoms/Icon.vue';

const emit = defineEmits(['item-click']);

defineProps({
  icon: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: false,
  },
  path: {
    type: String,
    default: null,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  subItems: {
    type: Array,
    default: () => [],
  },
  notificationCount: {
    type: Number,
    default: 0,
  },
  separator: {
    type: Boolean,
    default: false,
  },
});

const itemClicked = (item) => {
  if (!item.path || item.disabled) {
    return;
  }
  emit('item-click', item);
};

const buttonHovered = ref(false);
const hoveredSubItems = ref([]);
const hoveredItem = ref([]);

const hoverButton = (status) => {
  buttonHovered.value = status;
  if (!status) {
    hoveredSubItems.value = [];
    hoveredItem.value = {};
  }
};

const hover = (item) => {
  hoveredItem.value = item;
  if (!item?.subItems) {
    hoveredSubItems.value = [];
    return;
  }
  hoveredSubItems.value = item.subItems;
};

</script>

<style scoped lang="scss">
@import '@foundation';

.nav-item {
  position: relative;
}

.nav-button {
  @extend .padding-bottom_xs-df;
  @extend .padding-top_sm;
  @extend .padding-left_xs-df;
  @extend .padding-right_xs-df;

  color: map-get($neutral, 'neutral-400');
  border-bottom: 3px solid transparent;

  &:hover {
    border-bottom: 3px solid map-get($neutral, 'neutral-400');
    color: map-get($neutral, 'neutral-700');

    > i {
      color: map-get($neutral, 'neutral-700') !important;
    }
  }

  &-active {
    color: map-get($neutral, 'neutral-700');
    border-bottom: 3px solid map-get($neutral, 'neutral-700');
  }
}

.nav-subitems {
  min-width: 198px;
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  &.second-level {
    margin-left: 12px;
    padding-left: 12px;
    border-left: 1px solid map-get($neutral, 'neutral-200');
  }
  &-container {
    @extend .padding_xs-df;
    @extend .background_neutral_white;
    @extend .border_rounded_lg;
    @extend .border_stroke_sm_df;
    @extend .border_color_neutral_default;
    position: absolute;
    display: none;
    top: 100%;
    z-index: 15;
    &.opened {
      display: flex;
    }
  }
}

.nav-subitem {
  @extend .padding_xs-df;
  @extend .typography_default;
  @extend .medium-body-sm;
  cursor: pointer;
  white-space: nowrap;
  &:hover, &.hover {
    background-color: map-get($neutral, 'neutral-200');
  }
  &.text-disabled {
    cursor: default;
  }
}

.notifications__count {
  background-color: #d33d59;
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  font-weight: 600;
  font-size: 10px;
  z-index: 1;
  width: 20px;
  height: 20px;
  top: -6px;
  right: 8px;
}

.separator {
  width: 2px;
  height: 40px;
  background: map-get($neutral, 'neutral-200');
}
.nav-item-separator {
  margin-top: auto;
  margin-bottom: auto;
}
</style>
